﻿/**
 * 审计角色、审计组及依赖的纯函数。
 * 独立模块，无 React/UserManagement 依赖。
 *
 * 此为单一实现源文件：`src/constants/auditRolesAndGroups.js` 仅 re-export 本文件。
 * 生产包（宝塔/堡垒机）只含 `server/`，不得从 `src/` 引用，否则 Node 会 ERR_MODULE_NOT_FOUND。
 */

// 审计专栏模块 ID → 短名称
export const MODULE_ID_TO_SHORTNAME = {
  'project-manager': '项目', 'risk-matrix': '风险', 'data-warehouse': '数据仓库', 'risk-monitoring': '风险监控', 'org-chart': '组织架构', 'internal-control': '内控', 'duties': '职责', 'ippf': 'IPPF', 'it-audit': 'IT审计', 'compliance-gantt': '合规甘特', 'engineering-audit': '工程审计', 'marketing-audit': '营销审计', 'bidding-audit': '招投标', 'audit-workbench': '作业中心',
  'audit-comprehensive-budget': '预算审计', 'ai-chat': 'AI对话', 'automation-script-center': '脚本中心', 'audit-ai-chain': '作业链', 'contract-review': '合同审核', 'process-reference': '流程参照', 'agent-skill-library': '技能库', 'report-dashboard': '报告', 'pxcharts': '表格', 'model-library': '模型', 'strategic-analysis': '战略', 'audit-tool-library': '工具库', 'group-policy-library': '制度库', 'group-permission-library': '权限库', 'supervision-audit': '监察', 'rectification-center': '整改中心',
  'corn-decision': '玉米决策', 'feed-nutrition': '饲料测算', 'product-cost': '产品成本', 'supply-chain-cost': '供应链成本', 'production-efficiency': '生产效能', 'carcass-value': '胴体价值',
  'rolling-forecast': '滚动预测', 'roi-analysis': 'ROI', 'cvp-analysis': 'CVP',
  'user-management': '用户', 'database-manager': '数据库', 'ai-api-config': 'AI配置', 'audit-settings': '审计设置', 'backup-manager': '备份',
  'audit-log': '日志', 'security-monitor': '安全', };

const REMOVED_MODULE_IDS = new Set([
  'hedging-audit-supervision',
  'permission-manager',
  'data-lineage-center',
  'tool-navigator',
  'futures-trader',
  'brainstorm',
  'knowledge-base',
  'trendradar',
  'opinion-management',
  'feishu-meeting-workbench',
  'service-manager',
  'automation-script-center',
  'audit-ai-chain',
  'process-reference',
  'agent-skill-library',
  ]);

/** 权限管理中可配置的全部模块（与审计角色默认权限一览一致，供用户/角色/组权限配置使用） */
export const ALL_PERMISSION_MODULES = Object.entries(MODULE_ID_TO_SHORTNAME)
  .filter(([id]) => !REMOVED_MODULE_IDS.has(id))
  .map(([id, name]) => ({ id, name }));

/** 审计核心模块：按审计组时，默认所有审计组均可访问、编辑（无需单独配置） */
export const AUDIT_CORE_MODULE_IDS = [
  'project-manager', 'risk-matrix', 'data-warehouse', 'risk-monitoring', 'org-chart', 'internal-control', 'duties', 'ippf', 'it-audit', 'compliance-gantt', 'engineering-audit', 'marketing-audit', 'bidding-audit', 'audit-workbench',
];

export const MODULE_OPERATION_TYPES = [
  { id: 'view', name: '查看', description: '查看模块内容' },
  { id: 'search', name: '检索', description: '搜索、筛选' },
  { id: 'create', name: '新增', description: '新建记录' },
  { id: 'edit', name: '编辑', description: '修改已有记录' },
  { id: 'delete', name: '删除', description: '删除记录' }
];

const ALL_OPERATIONS = MODULE_OPERATION_TYPES.map(o => o.id);
const READ_ONLY_OPERATIONS = ['view', 'search'];
export const REGULAR_AUDITOR_ROLE_IDS = new Set([
  'audit-director',
  'audit-manager',
  'senior-auditor',
  'auditor',
  'junior-auditor',
]);
const PERMISSION_STORAGE_KEY = 'audit_permissions';

const RAW_AUDIT_ROLES = [
    { id: 'audit-director', name: '审计总监', description: '负责审计项目最终审批和团队管理', color: '#7c3aed', permissions: ['project-manager', 'risk-matrix', 'data-warehouse', 'risk-monitoring', 'org-chart', 'internal-control', 'duties', 'ippf', 'it-audit', 'compliance-gantt', 'engineering-audit', 'marketing-audit', 'bidding-audit', 'audit-workbench', 'audit-comprehensive-budget', 'ai-chat', 'automation-script-center', 'audit-ai-chain', 'contract-review', 'process-reference', 'agent-skill-library', 'report-dashboard', 'pxcharts', 'model-library', 'strategic-analysis', 'audit-tool-library', 'group-policy-library', 'feishu-meeting-workbench', 'group-permission-library', 'product-cost', 'supply-chain-cost', 'feed-nutrition', 'production-efficiency', 'carcass-value', 'rolling-forecast', 'roi-analysis', 'cvp-analysis', 'corn-decision', 'supervision-audit', 'rectification-center'], defaultOperations: ALL_OPERATIONS },
    { id: 'audit-manager', name: '审计经理', description: '负责审计项目管理和团队协调', color: '#8b5cf6', permissions: ['project-manager', 'risk-matrix', 'data-warehouse', 'risk-monitoring', 'org-chart', 'internal-control', 'duties', 'ippf', 'it-audit', 'compliance-gantt', 'engineering-audit', 'marketing-audit', 'bidding-audit', 'audit-workbench', 'audit-comprehensive-budget', 'ai-chat', 'automation-script-center', 'audit-ai-chain', 'contract-review', 'process-reference', 'agent-skill-library', 'report-dashboard', 'pxcharts', 'model-library', 'strategic-analysis', 'audit-tool-library', 'group-policy-library', 'feishu-meeting-workbench', 'group-permission-library', 'product-cost', 'supply-chain-cost', 'feed-nutrition', 'production-efficiency', 'carcass-value', 'rolling-forecast', 'roi-analysis', 'cvp-analysis', 'corn-decision', 'supervision-audit', 'rectification-center'], defaultOperations: ALL_OPERATIONS },
    { id: 'senior-auditor', name: '高级审计师', description: '执行复杂审计任务和风险评估', color: '#3b82f6', permissions: ['project-manager', 'risk-matrix', 'data-warehouse', 'risk-monitoring', 'org-chart', 'internal-control', 'duties', 'it-audit', 'compliance-gantt', 'engineering-audit', 'marketing-audit', 'bidding-audit', 'audit-workbench', 'audit-comprehensive-budget', 'ai-chat', 'automation-script-center', 'audit-ai-chain', 'contract-review', 'process-reference', 'agent-skill-library', 'report-dashboard', 'pxcharts', 'model-library', 'strategic-analysis', 'audit-tool-library', 'group-policy-library', 'feishu-meeting-workbench', 'group-permission-library', 'product-cost', 'supervision-audit', 'rectification-center'], defaultOperations: ALL_OPERATIONS },
    { id: 'auditor', name: '审计师', description: '执行日常审计工作', color: '#10b981', permissions: ['project-manager', 'risk-matrix', 'data-warehouse', 'risk-monitoring', 'org-chart', 'internal-control', 'it-audit', 'compliance-gantt', 'engineering-audit', 'marketing-audit', 'bidding-audit', 'audit-workbench', 'audit-comprehensive-budget', 'ai-chat', 'automation-script-center', 'audit-ai-chain', 'contract-review', 'process-reference', 'agent-skill-library', 'report-dashboard', 'pxcharts', 'model-library', 'strategic-analysis', 'audit-tool-library', 'group-policy-library', 'feishu-meeting-workbench', 'group-permission-library', 'supervision-audit', 'rectification-center'], defaultOperations: ALL_OPERATIONS },
    { id: 'junior-auditor', name: '初级审计师', description: '协助审计工作和数据收集', color: '#f59e0b', permissions: ['project-manager', 'it-audit', 'compliance-gantt', 'marketing-audit', 'audit-workbench', 'audit-comprehensive-budget', 'contract-review', 'process-reference', 'agent-skill-library', 'pxcharts', 'audit-tool-library', 'group-policy-library', 'feishu-meeting-workbench', 'group-permission-library', 'supervision-audit', 'rectification-center'], defaultOperations: ['view', 'search', 'create', 'edit'] },
  { id: 'data-analyst', name: '数据分析师', description: '负责数据采集和分析', color: '#06b6d4', permissions: ['data-warehouse', 'risk-monitoring', 'audit-tool-library', 'group-policy-library', 'feishu-meeting-workbench', 'group-permission-library', 'product-cost', 'supply-chain-cost', 'feed-nutrition', 'production-efficiency', 'carcass-value', 'rolling-forecast', 'roi-analysis', 'cvp-analysis', 'corn-decision', 'pxcharts', 'model-library', 'report-dashboard'], defaultOperations: ALL_OPERATIONS },
  { id: 'finance-temp', name: '财务临时账号(可编辑)', description: '可编辑风险与内控模块，其他能力由权限管理统一配置', color: '#0ea5e9', permissions: ['risk-matrix', 'internal-control'], defaultOperations: ['view', 'search', 'create', 'edit'] },
  { id: 'finance-temp-viewer', name: '财务临时账号(浏览)', description: '仅查看财务部门相关风险与内控数据', color: '#0284c7', permissions: ['risk-matrix', 'internal-control'], defaultOperations: READ_ONLY_OPERATIONS },
  { id: 'finance-temp-editor', name: '财务临时账号(编辑)', description: '可编辑风险与内控模块，不含删除', color: '#0369a1', permissions: ['risk-matrix', 'internal-control'], defaultOperations: ['view', 'search', 'create', 'edit'] },
  { id: 'finance-temp-manager', name: '财务临时账号(管理)', description: '可管理风险与内控模块（含删除）', color: '#075985', permissions: ['risk-matrix', 'internal-control'], defaultOperations: ALL_OPERATIONS },
  { id: 'viewer', name: '只读用户', description: '仅查看审计报告与流程参照', color: '#6b7280', permissions: ['report-dashboard', 'process-reference', 'agent-skill-library', 'audit-tool-library', 'group-policy-library', 'feishu-meeting-workbench', 'group-permission-library'], defaultOperations: READ_ONLY_OPERATIONS },
  { id: 'business-dept', name: '业务部门', description: '仅可访问分析模型库，可输入数据并使用各模型', color: '#0d9488', permissions: ['model-library', 'audit-tool-library', 'group-policy-library', 'feishu-meeting-workbench'], defaultOperations: ALL_OPERATIONS },
  { id: 'corn-drying-dept', name: '烘干玉米业务部门', description: '仅可访问分析模型库中的玉米采购决策模型（含因素分析）', color: '#b45309', permissions: ['model-library', 'audit-tool-library', 'group-policy-library', 'feishu-meeting-workbench'], defaultOperations: ALL_OPERATIONS, modelLibraryModelIds: ['corn-decision'] }
];

export const AUDIT_ROLES = RAW_AUDIT_ROLES.map((role) => ({
  ...role,
  permissions: (role.permissions || []).filter((id) => !REMOVED_MODULE_IDS.has(id)),
}));

/** 仅系统管理员全模块通行；审计总监/经理不再自动全量（权限一律来自用户配置） */
export function hasFullModuleAccess(user) {
  if (!user) return false;
  return user.role === 'admin';
}

export function isRegularAuditorAccount(user) {
  const roleId = String(user?.auditRole || '').trim().toLowerCase();
  if (!roleId) return false;
  if (roleId.startsWith('finance-temp')) return false;
  return REGULAR_AUDITOR_ROLE_IDS.has(roleId);
}

export const AUDIT_GROUPS = [
  { id: 'group1', name: '专项审计组' },
  { id: 'group3', name: '监察审计组' },
  { id: 'group2', name: '工程审计组' },
  { id: 'group4', name: '综合审计组' },
];

export function getAuditRolePermissionSummary(roleId) {
  const role = AUDIT_ROLES.find(r => r.id === roleId);
  if (!role || !role.permissions?.length) return [];
  return role.permissions.filter((id) => !REMOVED_MODULE_IDS.has(id)).map(id => MODULE_ID_TO_SHORTNAME[id] || id);
}

export function getAuditRoleOperationSummary(roleId) {
  const role = AUDIT_ROLES.find(r => r.id === roleId);
  if (!role) return '—';
  const ops = role.defaultOperations || ALL_OPERATIONS;
  if (ops.length >= 5) return '查看、检索、新增、编辑、删除';
  const names = MODULE_OPERATION_TYPES.filter(o => ops.includes(o.id)).map(o => o.name);
  return names.length ? names.join('、') : '仅查看';
}

function readPermissionConfig() {
  try {
    if (typeof localStorage === 'undefined') return {};
    return JSON.parse(localStorage.getItem(PERMISSION_STORAGE_KEY) || '{}');
  } catch (_) {
    return {};
  }
}

function parseActionPermissions(modulePermission) {
  if (!modulePermission || typeof modulePermission !== 'object') return null;
  if (modulePermission.allowed === true) return ALL_OPERATIONS;
  if (modulePermission.allowed === false) return [];
  const actions = Array.isArray(modulePermission.actions) ? modulePermission.actions.filter(Boolean) : [];
  return actions.length ? Array.from(new Set(actions)) : null;
}

/** 仅从「权限管理」本地存储中的 users[userId].function[moduleId] 读取细粒度操作；不再读取角色/审计组 */
function resolveFineGrainedModuleOperations(user, moduleId) {
  const permissionConfig = readPermissionConfig();
  if (!permissionConfig || typeof permissionConfig !== 'object') return null;

  const userEntry = permissionConfig?.users?.[user?.id]?.function?.[moduleId];
  const userActions = parseActionPermissions(userEntry);
  if (userActions) return userActions;
  if (userEntry?.allowed === false) return [];

  return null;
}

export function getModuleOperationsForUser(user, moduleId) {
  if (REMOVED_MODULE_IDS.has(moduleId)) return [];
  if (!user) return ALL_OPERATIONS;
  if (user.role === 'admin') return ALL_OPERATIONS;

  const fineGrainedOps = resolveFineGrainedModuleOperations(user, moduleId);
  if (Array.isArray(fineGrainedOps)) return fineGrainedOps;

  return [];

  // 已在用户管理中授权模块，但未在「权限管理」配置功能级：仅开放查看/检索（不继承审计角色模板）
}

export function canModuleOperation(user, moduleId, operation) {
  return getModuleOperationsForUser(user, moduleId).includes(operation);
}

export function getModelLibraryAllowedModelIds(user) {
  if (!user) return undefined;
  if (user.role === 'admin') return undefined;
  // 权限一律按用户：模型子集若需限制，应在用户权限 JSON / 后续扩展字段中配置；此处不再按审计角色裁剪
  return undefined;
}
